import React, { useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles, X, Copy, Check, ExternalLink, Loader,
} from 'lucide-react';

// ============================================================
// PERFORMANCE ANALYSIS MODAL
//
// Pattern matches the AI Workspace scaffold in LeadDetail.js — admin
// picks a period, clicks Generate, this modal opens with the full team
// data wrapped in a prompt template, Copy to Clipboard, opens the
// SoftRadix Sales Rebuild Claude project.
//
// Stateless: no API call to Anthropic, no save of past analyses, no
// scheduling. v1 is pure copy-paste handoff.
//
// CRITICAL — Phase 2/3 adoption gap (shipped 2026-05-13): discovery_done
// and proposal_sent counters come from daily_stats (calls_booked /
// proposals_sent) via the backend, NOT from lead-level stamps. Lead-level
// negotiation_at and closed_at ARE trustworthy and are used.
// ============================================================

// Same contract as LeadDetail's AI Workspace: open the logged-in user's own
// Claude project (users.claude_project_url, set in User Management), falling
// back to a fresh chat. This card is admin-gated today (Dashboard.js), so the
// old hardcoded project URL happened to be correct — but it broke silently for
// anyone else and pinned the link to one project id.
const CLAUDE_FALLBACK_URL = 'https://claude.ai/new';

const PERIOD_OPTIONS = [
  { key: 'this_week',    label: 'This Week' },
  { key: 'last_week',    label: 'Last Week' },
  { key: 'last_2_weeks', label: 'Last 2 Weeks' },
  { key: 'this_month',   label: 'This Month' },
  { key: 'last_month',   label: 'Last Month' },
  { key: 'custom',       label: 'Custom' },
];

// ---------- date math ----------
const fmt = (d) => d.toISOString().split('T')[0];
const getMonday = (d) => {
  const day = d.getDay();
  const offset = day === 0 ? 6 : day - 1;
  const monday = new Date(d);
  monday.setDate(d.getDate() - offset);
  return monday;
};
const addDays = (d, n) => {
  const out = new Date(d);
  out.setDate(d.getDate() + n);
  return out;
};

const getRangeFor = (period, custom) => {
  const today = new Date();
  switch (period) {
    case 'this_week': {
      const m = getMonday(today);
      return { start: fmt(m), end: fmt(addDays(m, 6)) };
    }
    case 'last_week': {
      const m = addDays(getMonday(today), -7);
      return { start: fmt(m), end: fmt(addDays(m, 6)) };
    }
    case 'last_2_weeks': {
      const start = addDays(getMonday(today), -14);
      const end = addDays(getMonday(today), -1);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'this_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: fmt(first), end: fmt(today) };
    }
    case 'last_month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: fmt(first), end: fmt(last) };
    }
    case 'custom':
      return { start: custom.start || '', end: custom.end || '' };
    default:
      return { start: '', end: '' };
  }
};

// ============================================================
// PROMPT BLOCK COMPOSERS
//
// Each composer takes the API response and returns a formatted string
// for one named placeholder in PROMPT_TEMPLATE. Keeping them separate
// makes the template easy to swap when product iterates on wording.
// ============================================================

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

const padRight = (s, n) => String(s).padEnd(n);
const padLeft  = (s, n) => String(s).padStart(n);

const composePeriodBlock = (data) => {
  const { period } = data;
  return [
    `Period: ${period.label}`,
    `Working days (Mon–Fri): ${period.working_days}`,
    `Dates: ${period.start} → ${period.end}`,
  ].join('\n');
};

const composeTargetsBlock = (data) => {
  const t = data.targets;
  return [
    'Targets for this period:',
    `  • Validated Hot leads: ${t.hot}`,
    `  • Discovery calls held: ${t.discovery_done}`,
    `  • Proposals sent: ${t.proposal_sent}`,
    `  • Negotiations entered: ${t.negotiation}`,
    `  • Deals closed-won: ${t.closed_won}`,
  ].join('\n');
};

const composePerBdeActivityBlock = (data) => {
  const headers = ['Name', 'CR', 'Acc', 'Repl', 'HO', 'Calls', 'Prop', 'Leads', 'Days'];
  const widths  = [20, 5, 5, 5, 4, 6, 5, 6, 5];
  const lines = [
    headers.map((h, i) => padRight(h, widths[i])).join(' '),
    widths.map((w) => '-'.repeat(w)).join(' '),
    ...data.per_bde_activity.map((r) => [
      padRight(r.name, widths[0]),
      padLeft(r.cr_sent, widths[1]),
      padLeft(r.accepted, widths[2]),
      padLeft(r.replies, widths[3]),
      padLeft(r.handoffs, widths[4]),
      padLeft(r.calls, widths[5]),
      padLeft(r.proposals, widths[6]),
      padLeft(r.leads_added, widths[7]),
      padLeft(r.days_logged, widths[8]),
    ].join(' ')),
  ];
  return lines.join('\n');
};

const composeTeamTotalsBlock = (data) => {
  const t = data.team_totals;
  return [
    'TEAM TOTALS',
    `  Connection requests sent: ${t.cr_sent}`,
    `  Accepted: ${t.accepted} (${pct(t.accepted, t.cr_sent)}%)`,
    `  Replies: ${t.replies} (${pct(t.replies, t.cr_sent)}%)`,
    `  Handoffs: ${t.handoffs}`,
    `  Calls held: ${t.calls}`,
    `  Proposals sent: ${t.proposals}`,
    `  Leads added: ${t.leads_added}`,
    `  Days logged across team: ${t.days_logged}`,
  ].join('\n');
};

const composePipelinePeriodBlock = (data) => {
  const p = data.pipeline_period;
  const accLines = p.classification_accuracy_per_bde.length === 0
    ? ['  (No classification picks with AI review in this period.)']
    : p.classification_accuracy_per_bde.map((a) =>
        `  • ${a.name}: ${a.matches}/${a.picks} (${a.accuracy_pct}%)`
      );
  return [
    'PIPELINE (this period)',
    `  New validated Hot: ${p.new_hot}`,
    `  New validated Warm: ${p.new_warm}`,
    `  New validated Cold: ${p.new_cold}`,
    `  Pending validation (all-time): ${p.pending_validation}`,
    '',
    'Classification accuracy (BDE pick vs AI review):',
    ...accLines,
  ].join('\n');
};

const composeFunnelTransitionsBlock = (data) => {
  const f = data.funnel_transitions;
  return [
    'FUNNEL TRANSITIONS',
    `  Discovery calls held (daily_stats): ${f.discovery_done_from_daily_stats}`,
    `  Proposals sent (daily_stats): ${f.proposal_sent_from_daily_stats}`,
    `  Negotiations entered (lead-level): ${f.negotiation_lead_level}`,
    `  Closed-won: ${f.closed_won}`,
    `  Closed-lost: ${f.closed_lost}`,
    '',
    'Note: discovery_done / proposal_sent come from daily_stats activity',
    'logs, not from lead status. The lead-status dropdown is in adoption',
    'rollout — counts from those columns will be artificially low until',
    'the team has had ~4 weeks to build the habit (Phase 2/3 shipped May 13).',
  ].join('\n');
};

const composeDealValuesBlock = (data) => {
  const d = data.deal_values;
  const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
  return [
    'DEAL VALUES',
    `  Open pipeline (total):       ${inr(d.open_total)}`,
    `  Open pipeline (weighted):    ${inr(d.open_weighted)}`,
    `  Closed-won this period:      ${inr(d.closed_won_period_value)}`,
    `  Avg age of open deals:       ${d.avg_deal_age_days} days`,
  ].join('\n');
};

const composeQualityBlock = (data) => {
  const reply = data.quality.reply_rates_per_bde.map((r) =>
    `  • ${r.name}: ${r.replies}/${r.cr_sent} (${r.reply_rate_pct}%)`
  ).join('\n');
  const overdue = data.quality.overdue_followups_per_bde.map((r) =>
    `  • ${r.name}: ${r.overdue_count} overdue, ${r.due_this_week_count} due this week`
  ).join('\n');
  return [
    'QUALITY',
    '',
    'Reply rates per BDE:',
    reply || '  (no activity)',
    '',
    'Follow-up health per BDE:',
    overdue || '  (no follow-ups)',
  ].join('\n');
};

const composeStalledBlock = (data) => {
  const s = data.stalled;
  const buckets = [
    ['Active leads with no update in 7+ days',
     s.active_no_update_7d],
    ['Discovery scheduled 5+ days ago, never marked done',
     s.discovery_scheduled_5d_no_done],
    ['Proposal sent 14+ days ago, no negotiation marked',
     s.proposal_sent_14d_no_negotiation],
  ];
  const lines = ['STALLED'];
  for (const [label, b] of buckets) {
    lines.push('');
    lines.push(`${label}: ${b.count}`);
    if (b.sample_names.length) {
      lines.push(`  Examples: ${b.sample_names.join(', ')}`);
    }
  }
  return lines.join('\n');
};

const buildBlocks = (data) => ({
  period_block:             composePeriodBlock(data),
  targets_block:            composeTargetsBlock(data),
  per_bde_activity_block:   composePerBdeActivityBlock(data),
  team_totals_block:        composeTeamTotalsBlock(data),
  pipeline_period_block:    composePipelinePeriodBlock(data),
  funnel_transitions_block: composeFunnelTransitionsBlock(data),
  deal_values_block:        composeDealValuesBlock(data),
  quality_block:            composeQualityBlock(data),
  stalled_block:            composeStalledBlock(data),
});

// ============================================================
// PROMPT TEMPLATE
//
// TODO: replace this scaffold with the product-approved template body.
// Placeholders use the `{name_block}` form and map 1:1 onto buildBlocks
// above. Adding a new placeholder = adding a new composer + the key in
// buildBlocks; the substitution step doesn't need to change.
// ============================================================
const PROMPT_TEMPLATE = `You are helping Prateek Arora (Business Head at SoftRadix) analyze the sales team's performance for a chosen period.

DO NOT narrate or acknowledge this framing — read it, then deliver the analysis.

{period_block}

{targets_block}

PER-BDE ACTIVITY
{per_bde_activity_block}

{team_totals_block}

{pipeline_period_block}

{funnel_transitions_block}

{deal_values_block}

{quality_block}

{stalled_block}

Now give Prateek:
  1. A 3-bullet headline assessment for the period.
  2. The top two risks you see (with names where relevant).
  3. The single most leveraged intervention for next week.
Be specific and direct. Push back if the data suggests Prateek's priorities are wrong.`;

const substitute = (template, blocks) => {
  return template.replace(/\{(\w+)\}/g, (m, key) => {
    if (Object.prototype.hasOwnProperty.call(blocks, key)) return blocks[key];
    return m;
  });
};

// ============================================================
// COMPONENT
// ============================================================

const PerformanceAnalysisModal = ({ open, onClose, data }) => {
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();

  if (!open || !data) return null;

  const claudeUrl = user?.claude_project_url || CLAUDE_FALLBACK_URL;

  const prompt = substitute(PROMPT_TEMPLATE, buildBlocks(data));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      alert('Could not copy to clipboard. Select and copy manually.');
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50, padding: 16,
      }}
    >
      <div style={{
        background: '#FFFFFF', borderRadius: 8,
        maxWidth: 820, width: '100%', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: 16, borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={18} /> Performance Analysis
            </h3>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
              {data.period.label} · Copy the prompt, then open Claude to discuss.
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280',
          }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6,
            padding: 12, fontSize: 12, color: '#374151',
            maxHeight: 480, overflow: 'auto', fontFamily: 'inherit',
            margin: 0,
          }}>{prompt}</pre>
        </div>

        <div style={{
          padding: 16, borderTop: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={handleCopy} className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {copied
              ? <><Check size={14} /> Copied</>
              : <><Copy size={14} /> Copy to Clipboard</>}
          </button>
          <a href={claudeUrl} target="_blank" rel="noopener noreferrer"
             className="btn btn-primary"
             style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ExternalLink size={14} /> Open in Claude Project
          </a>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// LAUNCHER — the card that lives on the Admin Dashboard.
// Owns the period dropdown + Generate button + fetch + modal open/close.
// ============================================================

export const PerformanceAnalysisCard = () => {
  const [period, setPeriod] = useState('last_week');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const range = getRangeFor(period, custom);
  const canGenerate = !loading && range.start && range.end && range.start <= range.end;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/stats/performance-analysis', {
        params: { start: range.start, end: range.end },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate analysis.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={18} /> Performance Analysis
            </h3>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
              Generate a full team-performance prompt for Claude — pick a
              period, click Generate, copy the prompt into the Sales Rebuild
              project.
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end',
          gap: 12, marginTop: 16,
        }}>
          <div>
            <label style={{
              display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4,
            }}>Period</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{
                padding: '6px 10px', border: '1px solid #D1D5DB',
                borderRadius: 6, fontSize: 14, background: '#FFFFFF', cursor: 'pointer',
              }}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>

          {period === 'custom' && (
            <>
              <div>
                <label style={{
                  display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4,
                }}>Start</label>
                <input
                  type="date"
                  value={custom.start}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={{
                    padding: '5px 8px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 14,
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4,
                }}>End</label>
                <input
                  type="date"
                  value={custom.end}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={{
                    padding: '5px 8px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 14,
                  }}
                />
              </div>
            </>
          )}

          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            className="btn btn-primary"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: canGenerate ? 1 : 0.6,
              cursor: canGenerate ? 'pointer' : 'default',
            }}
          >
            {loading
              ? <><Loader size={14} /> Generating…</>
              : <><Sparkles size={14} /> Generate Analysis</>}
          </button>

          {range.start && range.end && (
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              Range: {range.start} → {range.end}
            </div>
          )}
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: 8, background: '#FEE2E2',
            color: '#991B1B', borderRadius: 6, fontSize: 13,
          }}>
            {error}
          </div>
        )}
      </div>

      <PerformanceAnalysisModal
        open={!!data}
        data={data}
        onClose={() => setData(null)}
      />
    </>
  );
};

export default PerformanceAnalysisModal;
